import {
  Box,
  Checkbox,
  createStyles,
  Divider,
  FormControlLabel,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { AddCircleOutline, Close } from '@material-ui/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import api from 'api';
import { useMakes } from 'api/organizations/plugins/velocityengage/folioTemplate';
import strings from 'common/strings';
import LoadingIndicator from 'components/shared/LoadingIndicator';
import { FolioTemplate, FolioTemplateListOption } from 'models';

import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import VelocityEngageMediaTab from '../VelocityEngageMediaTab/VelocityEngageMediaTab';
import AddCustom from './AddCustom';
import PriceSettings from './PriceSettings';

import './FolioTemplate.scss';

const useStyles = makeStyles(() =>
  createStyles({
    folioSelector: {
      width: '100%',
    },
    loadingContainer: {
      height: '100%',
    },
  })
);

interface FolioTemplateContainerProps {
  orgId: string;
}

const FolioTemplateContainer = ({ orgId }: FolioTemplateContainerProps) => {
  const templatePlaceholder = useMemo(
    () => ({
      id: 'none',
      type: 'None',
      tenantId: '',
      v1: true,
    }),
    []
  );
  const addStandardTemplateOption = {
    id: 'addStandard',
    type: 'Add Standard',
    tenantId: '',
    v1: true,
  };
  const standardTypePlaceholder = {
    id: 'none',
    type: 'Select Type',
    tenantId: '',
    v1: true,
  };
  const [selectedTemplate, setSelectedTemplate] =
    useState<FolioTemplateListOption>(templatePlaceholder);
  const [templateListData, setTemplateListData] = useState<
    FolioTemplateListOption[]
  >([]);
  const [templateSelectorOptions, setTemplateSelectorOptions] = useState<
    JSX.Element[]
  >([]);
  const [standardTypeSelectorOptions, setStandardTypeSelectorOptions] =
    useState<JSX.Element[]>([]);
  const [folioTemplateData, setFolioTemplateData] = useState<FolioTemplate>();
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [vasStandardTemplates, setVasStandardTemplates] =
    useState<FolioTemplateListOption[]>();
  const [selectedFolioTemplate, setSelectedFolioTemplate] =
    useState<FolioTemplateListOption>(standardTypePlaceholder);
  const [showStandardTemplateSelector, setShowStandardTemplateSelector] =
    useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isOpenUnsavedChanges, setIsOpenUnsavedChanges] = useState(false);
  const [unsavedChangeEvent, setUnsavedChangeEvent] = useState<string>();
  type UnsavedEventSource = 'templateChange' | 'typeChange';
  const [unsavedEventSource, setUnsavedEventSource] =
    useState<UnsavedEventSource>();
  const [isOpenConfirmDelete, setIsOpenConfirmDelete] = useState(false);
  const classes = useStyles();
  const [selectedMake, setSelectedMake] = useState<string>();
  const { data: makesData } = useMakes(orgId);

  const getTemplateList = useCallback(
    async (id: string) => {
      try {
        const data =
          await api.organizations.plugins.velocityengage.folioTemplate.getFolioTemplateListOptions(
            { orgId: id }
          );
        setTemplateListData(data);
        const currentOrgTemplates: FolioTemplateListOption[] = data.filter(
          (template) => template.tenantId === orgId
        );
        const vasTemplates: FolioTemplateListOption[] = data.filter(
          (template) => template.tenantId !== orgId
        );

        setVasStandardTemplates(vasTemplates);

        let options: JSX.Element[] = [];
        if (currentOrgTemplates.length === 0) {
          options.push(
            <MenuItem
              key={templatePlaceholder.id}
              value={templatePlaceholder.id}
            >
              {templatePlaceholder.type}
            </MenuItem>
          );
        } else {
          options = currentOrgTemplates.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.type} {option.make}
            </MenuItem>
          ));
        }
        if (vasTemplates.length > 0) {
          options.push(
            <MenuItem
              key={addStandardTemplateOption.id}
              value={addStandardTemplateOption.id}
            >
              {addStandardTemplateOption.type}
            </MenuItem>
          );
        }

        setTemplateSelectorOptions(options);

        setIsLoadingTemplate(true);
        if (currentOrgTemplates.length > 0) {
          const defaultTemplate: FolioTemplateListOption =
            currentOrgTemplates[0];
          const setDefault =
            await api.organizations.plugins.velocityengage.folioTemplate.getFolioTemplate(
              {
                orgId: defaultTemplate.tenantId,
                folioTemplateId: defaultTemplate.id,
                isV2: !defaultTemplate.v1,
              }
            );
          setFolioTemplateData(setDefault);
          setSelectedTemplate(defaultTemplate);
        } else {
          setSelectedTemplate(templatePlaceholder);
        }
      } catch (e: any) {
        setMessage(e.message);
        setOpen(true);
      } finally {
        setIsLoadingTemplate(false);
      }
    },
    [
      addStandardTemplateOption.id,
      addStandardTemplateOption.type,
      orgId,
      templatePlaceholder,
    ]
  );

  useEffect(() => {
    getTemplateList(orgId);
  }, [getTemplateList, orgId]);

  const handleOpenUnsavedChanges = (
    selectorValue: string,
    source: UnsavedEventSource
  ) => {
    setIsOpenUnsavedChanges(true);
    setUnsavedChangeEvent(selectorValue);
    setUnsavedEventSource(source);
  };

  const handleAddStandardClick = async () => {
    if (vasStandardTemplates) {
      let options: JSX.Element[] = [];
      options = vasStandardTemplates
        .filter(
          (t) =>
            !templateListData.find(
              (existingTemplate) =>
                t.type === existingTemplate.type &&
                existingTemplate.make === null &&
                t.tenantId !== existingTemplate.tenantId
            )
        )
        .map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.type}
          </MenuItem>
        ));
      setStandardTypeSelectorOptions(options);
      setShowStandardTemplateSelector(true);
      await setSelectedTemplate(addStandardTemplateOption);
    }
  };

  const handleChangeTemplate = async (
    selectorValue: string,
    checkUnsaved: boolean = true
  ) => {
    if (
      checkUnsaved &&
      selectedTemplate?.id === addStandardTemplateOption.id &&
      selectedFolioTemplate.id !== standardTypePlaceholder.id
    ) {
      return handleOpenUnsavedChanges(selectorValue, 'templateChange');
    }
    setShowStandardTemplateSelector(false);
    setSelectedFolioTemplate(standardTypePlaceholder);
    if (selectorValue === 'addStandard') {
      return handleAddStandardClick();
    }
    const matchingTemplate = templateListData.find(
      (template) => template.id === selectorValue
    );
    if (!matchingTemplate) {
      setShowStandardTemplateSelector(false);
      setSelectedTemplate(templatePlaceholder);
      setFolioTemplateData(undefined);
      return;
    }
    setSelectedTemplate(matchingTemplate);
    if (matchingTemplate) {
      setIsLoadingTemplate(true);
      try {
        const data =
          await api.organizations.plugins.velocityengage.folioTemplate.getFolioTemplate(
            {
              orgId: matchingTemplate.tenantId,
              folioTemplateId: matchingTemplate.id,
              isV2: !matchingTemplate.v1,
            }
          );
        setFolioTemplateData(data);
      } catch (e: any) {
        setMessage(e.message);
        setOpen(true);
      } finally {
        setIsLoadingTemplate(false);
      }
    }
  };

  const handleChangeMake = async (make: string) => {
    setSelectedMake(make);

    const existingTemplatesForMake = templateListData
      .filter((template) => template.make === make)
      .map((template) => template.type);

    const uncreatedTemplatesForMake = vasStandardTemplates?.filter(
      (template) =>
        !existingTemplatesForMake.find((type) => template.type === type)
    );

    if (uncreatedTemplatesForMake) {
      let options: JSX.Element[] = [];
      options = uncreatedTemplatesForMake.map((t) => (
        <MenuItem key={t.id} value={t.id}>
          {t.type}
        </MenuItem>
      ));
      setStandardTypeSelectorOptions(options);
    }
  };

  const folioTemplateId = folioTemplateData?.id;
  const [customPriceLabel, setCustomPriceLabel] = useState<string>();
  const [additionalPrices, setAdditionalPrices] = useState<string>();
  useEffect(() => {
    setAdditionalPrices(folioTemplateData?.settings?.ADDITIONAL_PRICES);
    setCustomPriceLabel(folioTemplateData?.settings?.PRICE_LABEL);
  }, [folioTemplateData]);

  const priceTypeToShowFromServer = useMemo<
    'MSRP' | 'retail' | 'internet' | 'CallForPrice'
  >(() => {
    // modifying existing value
    const priceTypeValue = folioTemplateData?.settings?.PRICE_TYPE;
    if (
      priceTypeValue !== undefined &&
      (priceTypeValue === 'MSRP' ||
        priceTypeValue === 'retail' ||
        priceTypeValue === 'internet' ||
        priceTypeValue === 'CallForPrice')
    ) {
      return priceTypeValue;
    }
    // general logic for getting default price type
    function priceTypeToShow(
      templateOption: FolioTemplateListOption
    ): 'MSRP' | 'retail' | 'internet' | 'CallForPrice' {
      if (templateOption.type !== '') {
        if (templateOption.type.includes('NEW')) {
          return 'MSRP';
        }
        if (templateOption.type.includes('DEFAULT')) {
          return 'MSRP';
        }
        return 'retail';
      }
      // fallback
      return 'retail';
    }
    // currently choosing from "add standard".
    if (selectedFolioTemplate?.id && selectedFolioTemplate?.id !== 'none') {
      return priceTypeToShow(selectedFolioTemplate);
    }
    // default for when "create standard" is not selected
    return priceTypeToShow(selectedTemplate);
  }, [folioTemplateData, selectedTemplate, selectedFolioTemplate]);

  const [priceTypeToShow, setPriceTypeToShow] = useState<
    'MSRP' | 'retail' | 'internet' | 'CallForPrice'
  >(priceTypeToShowFromServer);
  useEffect(() => {
    setPriceTypeToShow(priceTypeToShowFromServer);
  }, [priceTypeToShowFromServer, setPriceTypeToShow, folioTemplateId]);

  const showSnapcellVideoFromServer =
    folioTemplateData?.settings?.SHOW_SNAPCELL_VIDEOS !== '0';
  const [showSnapcellVideoIfAvailable, setShowSnapcellVideoIfAvailable] =
    useState<boolean>(showSnapcellVideoFromServer);
  useEffect(() => {
    setShowSnapcellVideoIfAvailable(showSnapcellVideoFromServer);
  }, [showSnapcellVideoFromServer]);

  const handleClickSave = async () => {
    setIsOpenUnsavedChanges(false);
    if (folioTemplateData) {
      try {
        setIsLoadingTemplate(true);
        if (
          showStandardTemplateSelector &&
          selectedFolioTemplate.id !== 'none'
        ) {
          // POST
          if (selectedMake) folioTemplateData.make = selectedMake;
          const newTemplate =
            await api.organizations.plugins.velocityengage.folioTemplate.createFolioTemplate(
              {
                orgId,
                folioTemplate: {
                  ...folioTemplateData,
                  settings: {
                    PRICE_LABEL: customPriceLabel ?? '',
                    PRICE_TYPE: priceTypeToShow,
                    ADDITIONAL_PRICES: additionalPrices ?? '',
                    SHOW_SNAPCELL_VIDEOS: showSnapcellVideoIfAvailable
                      ? '1'
                      : '0',
                    VEHICLE_SPECS_ORDER:
                      folioTemplateData?.settings?.VEHICLE_SPECS_ORDER ?? '5',
                    RECOMMENDED_VEHICLE_ORDER:
                      folioTemplateData?.settings?.RECOMMENDED_VEHICLE_ORDER ??
                      '1',
                  },
                },
              }
            );

          // Add newly created folio to template selector.
          setTemplateSelectorOptions([
            <MenuItem key={newTemplate.id} value={newTemplate.id}>
              {newTemplate.type} {newTemplate.make}
            </MenuItem>,
            ...templateSelectorOptions,
          ]);
          setMessage(`${selectedFolioTemplate?.type} template saved.`);
          // Select the newly added option.
          setSelectedTemplate({
            ...selectedFolioTemplate,
            id: newTemplate.id,
            tenantId: newTemplate.tenantId,
          });
          // Remove the created type from the list of uncreated types.
          setVasStandardTemplates(
            vasStandardTemplates?.filter(
              (t) => t.id !== selectedFolioTemplate.id
            )
          );
          setTemplateListData(
            templateListData.map((t) => {
              if (t.id === selectedFolioTemplate.id) {
                return {
                  ...t,
                  id: newTemplate.id,
                  tenantId: newTemplate.tenantId,
                };
              }
              return t;
            })
          );
          // Reset selected standard to placeholder.
          setSelectedFolioTemplate(standardTypePlaceholder);
          setShowStandardTemplateSelector(false);
          setFolioTemplateData({
            ...newTemplate,
            type: folioTemplateData.type,
          });
        } else {
          // PUT
          await api.organizations.plugins.velocityengage.folioTemplate.updateFolioTemplate(
            {
              orgId,
              folioTemplateId: folioTemplateData.id,
              folioTemplate: {
                ...folioTemplateData,
                settings: {
                  PRICE_LABEL: customPriceLabel ?? '',
                  PRICE_TYPE: priceTypeToShow,
                  ADDITIONAL_PRICES: additionalPrices ?? '',
                  SHOW_SNAPCELL_VIDEOS: showSnapcellVideoIfAvailable
                    ? '1'
                    : '0',
                  VEHICLE_SPECS_ORDER:
                    folioTemplateData?.settings?.VEHICLE_SPECS_ORDER ?? '5',
                  RECOMMENDED_VEHICLE_ORDER:
                    folioTemplateData?.settings?.RECOMMENDED_VEHICLE_ORDER ??
                    '1',
                },
              },
            }
          );
          setMessage(`${selectedTemplate?.type} template saved.`);
        }
      } catch (error: any) {
        setMessage(error.message);
      } finally {
        setOpen(true);
        setIsLoadingTemplate(false);
      }
    }
  };

  const handleClose = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  const handleChangeStandardTemplateType = async (
    selectorValue: string,
    checkUnsaved: boolean = true
  ) => {
    if (
      checkUnsaved &&
      selectedTemplate?.id === addStandardTemplateOption.id &&
      selectedFolioTemplate.id !== standardTypePlaceholder.id
    ) {
      return handleOpenUnsavedChanges(selectorValue, 'typeChange');
    }
    const matchingTemplate = vasStandardTemplates?.find(
      (template) => template.id === selectorValue
    );
    if (!matchingTemplate) {
      return console.error(
        `Standard template id ${selectorValue} not found in list of uncreated standard templates.`
      );
    }
    if (matchingTemplate) {
      setIsLoadingTemplate(true);
      await setSelectedFolioTemplate(matchingTemplate);
      const data =
        await api.organizations.plugins.velocityengage.folioTemplate.getFolioTemplate(
          {
            orgId: matchingTemplate.tenantId,
            folioTemplateId: matchingTemplate.id,
            isV2: !matchingTemplate.v1,
          }
        );
      setFolioTemplateData(data);
      setIsLoadingTemplate(false);
    }
  };

  const handleCancelUnsavedChanges = async () => {
    await setIsOpenUnsavedChanges(false);
    if (unsavedChangeEvent) {
      if (unsavedEventSource === 'templateChange') {
        await handleChangeTemplate(unsavedChangeEvent, false);
      } else {
        await handleChangeStandardTemplateType(unsavedChangeEvent, false);
      }
    }
  };

  const handleDeleteTemplate = async () => {
    if (folioTemplateData) {
      try {
        setIsLoadingTemplate(true);
        await api.organizations.plugins.velocityengage.folioTemplate.deleteFolioTemplate(
          {
            orgId,
            folioTemplateId: folioTemplateData.id,
          }
        );

        setMessage(`${selectedTemplate?.type} template deleted.`);
        await getTemplateList(orgId);
      } catch (error: unknown) {
        if (
          error instanceof Error &&
          'message' in error &&
          typeof error.message === 'string'
        ) {
          setMessage(error.message);
        }
      } finally {
        setOpen(true);
        setIsLoadingTemplate(false);
        setIsOpenConfirmDelete(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setIsOpenConfirmDelete(false);
  };

  const handleClickDelete = async () => {
    setIsOpenConfirmDelete(true);
  };

  const getFolioContentHeader = () => {
    if (folioTemplateData) {
      if (
        showStandardTemplateSelector &&
        selectedFolioTemplate?.id !== 'none'
      ) {
        return selectedFolioTemplate?.type;
      }
      if (showStandardTemplateSelector) {
        return '';
      }
      return selectedTemplate?.type;
    }
    return '';
  };

  const getFolioContentBody = () => {
    if (
      folioTemplateData &&
      !(showStandardTemplateSelector && selectedFolioTemplate?.id === 'none')
    ) {
      return (
        <VelocityEngageMediaTab
          key={folioTemplateData.id}
          folioId={folioTemplateData.id}
          orgId={folioTemplateData.tenantId}
          folioTemplateData={folioTemplateData}
          setFolioTemplateData={setFolioTemplateData}
        />
      );
    }

    if (showStandardTemplateSelector) {
      return (
        <>
          <Box display="flex" justifyContent="center">
            <Typography variant="h4">
              Select an available template type to add
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center">
            <Typography variant="body1">
              Only templates that do not already exist are available to be
              added.
            </Typography>
          </Box>
        </>
      );
    }

    return (
      <>
        <Box display="flex" justifyContent="center">
          <Typography variant="h4">
            Create a template to get started.
          </Typography>
        </Box>
        <Box display="flex" justifyContent="center">
          <Typography variant="body1">
            Templates allow you to assign a predefined set of documents to
            display for different groups of vehicles in your inventory.
          </Typography>
        </Box>
        <Box display="flex" justifyContent="center">
          <AddCircleOutline onClick={handleAddStandardClick} />
        </Box>
      </>
    );
  };

  return (
    <>
      <div className="templateSelection">
        <Grid container spacing={3}>
          <Grid item xs={8}>
            <div className="filter-section">
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <div className="filter-selection-wrapper">
                    <InputLabel htmlFor="folio-template-select">
                      Folio Template
                    </InputLabel>
                    <Select
                      label="Folio Template"
                      inputProps={{
                        name: 'folio-template',
                        id: 'folio-template-select',
                      }}
                      value={selectedTemplate?.id}
                      onChange={(event) =>
                        handleChangeTemplate(event.target.value as string)
                      }
                      variant="outlined"
                      className={classes.folioSelector}
                    >
                      {templateSelectorOptions}
                    </Select>
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div className="filter-selection-wrapper">
                    {showStandardTemplateSelector && (
                      <div>
                        <InputLabel htmlFor="add-standard-type-select">
                          Type
                        </InputLabel>
                        <Select
                          label="Type"
                          inputProps={{
                            name: 'add-standard-type-select',
                            id: 'add-standard-type-select',
                          }}
                          value={selectedFolioTemplate?.id}
                          onChange={(event) =>
                            handleChangeStandardTemplateType(
                              event.target.value as string,
                              true
                            )
                          }
                          variant="outlined"
                        >
                          <MenuItem
                            key={standardTypePlaceholder.id}
                            value={standardTypePlaceholder.id}
                          >
                            {standardTypePlaceholder.type}
                          </MenuItem>
                          {standardTypeSelectorOptions}
                        </Select>
                      </div>
                    )}
                    {showStandardTemplateSelector &&
                      makesData?.data &&
                      makesData.data.length > 0 && (
                        <div>
                          <InputLabel htmlFor="make-select">Make</InputLabel>
                          <Select
                            label="Make"
                            inputProps={{
                              name: 'make-select',
                              id: 'make-select',
                            }}
                            value={selectedFolioTemplate?.make}
                            onChange={(event) =>
                              handleChangeMake(event.target.value as string)
                            }
                            variant="outlined"
                          >
                            {makesData.data.map((t) => (
                              <MenuItem key={t} value={t}>
                                {t}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                      )}
                  </div>
                </Grid>
              </Grid>
            </div>
            <div className="addCustom-Selection-Container">
              {selectedTemplate?.type === 'Add Custom' ? <AddCustom /> : ''}
            </div>
          </Grid>
          {/*TODO: re-enable checkbox when service is updated to allow updates of existing inventory. */}
          {/*<Grid item xs={4}>*/}
          {/* <Box display="flex" justifyContent="flex-end" alignContent="flex-end">*/}
          {/*   <FormControl component="fieldset">*/}
          {/*     <FormGroup>*/}
          {/*       <FormControlLabel*/}
          {/*         control={*/}
          {/*           <Checkbox*/}
          {/*             checked={processCurrentInventory}*/}
          {/*             onChange={event => setProcessCurrentInventory(event.target.checked)}*/}
          {/*             name="disclaimer"*/}
          {/*           />}*/}
          {/*         label="Update existing vehicles using this template."*/}
          {/*       />*/}
          {/*     </FormGroup>*/}
          {/*   </FormControl>*/}
          {/* </Box>*/}
          {/*</Grid>*/}
        </Grid>
      </div>
      <Divider />
      {isLoadingTemplate ? (
        <LoadingIndicator />
      ) : (
        <>
          <div className="folioContainer">
            <Typography variant="h4">{getFolioContentHeader()}</Typography>
            {folioTemplateData && (
              <>
                {!showStandardTemplateSelector && (
                  <button
                    type="button"
                    className="button cancel"
                    onClick={handleClickDelete}
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  className="button"
                  onClick={handleClickSave}
                  disabled={isLoadingTemplate}
                  style={
                    isLoadingTemplate
                      ? {
                          boxShadow: 'none',
                          opacity: 0.1,
                          cursor: 'not-allowed',
                        }
                      : {}
                  }
                >
                  Save
                </button>
              </>
            )}
            <Snackbar
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              open={open}
              autoHideDuration={6000}
              onClose={handleClose}
              message={message}
              action={
                <>
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={handleClose}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </>
              }
            />
          </div>
          {folioTemplateData &&
            !(
              showStandardTemplateSelector &&
              selectedFolioTemplate?.id === 'none'
            ) && (
              <>
                <PriceSettings
                  customPriceLabel={customPriceLabel}
                  setCustomPriceLabel={setCustomPriceLabel}
                  priceTypeToShow={priceTypeToShow}
                  setPriceTypeToShow={setPriceTypeToShow}
                  additionalPrices={additionalPrices}
                  setAdditionalPrices={setAdditionalPrices}
                />
                <h2>{strings.SHOW_SNAPCELL_VIDEOS}</h2>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showSnapcellVideoIfAvailable}
                      onChange={(event) => {
                        setShowSnapcellVideoIfAvailable(event.target.checked);
                      }}
                      name="show-snapcell-video"
                    />
                  }
                  label="Show snapcell video if available"
                />
              </>
            )}
          <div>{getFolioContentBody()}</div>
          <ConfirmationDialog
            openState={isOpenUnsavedChanges}
            onCancel={handleCancelUnsavedChanges}
            onConfirm={handleClickSave}
            titleText="Unsaved Changes"
            bodyText="You have unsaved changes on this page. Do you wish to save them before proceeding?"
          />
          <ConfirmationDialog
            openState={isOpenConfirmDelete}
            onCancel={handleCancelDelete}
            onConfirm={handleDeleteTemplate}
            titleText="Delete Folio Template"
            bodyText={`Are you sure you want to delete template: ${selectedTemplate.type}`}
          />
        </>
      )}
    </>
  );
};

export default FolioTemplateContainer;
